import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import { Icon } from "../../components/common/Icon";
import { BirthAnimation } from "../../components/create";
import { useAgentStore, useUIStore, toast } from "../../store";
import { legacyApi } from "../../api/legacy";
import { agentApi } from "../../api/agents";
import type { LegacyPack } from "../../types/legacy";
import { extractErrorMessage } from "../../utils/error";

/* ═══════════════════════════════════════════════════════════════
   Domain & Recommendation Data
   ═══════════════════════════════════════════════════════════════ */

interface DomainOption {
  id: string;
  icon: string;
  label: string;
  desc: string;
}

interface AgentRec {
  name: string;
  tags: string[];
  desc: string;
  seed: string;
}

interface GoalSuggestion {
  text: string;
}

const DOMAINS: DomainOption[] = [
  { id: "academic", icon: "school", label: "学术研究", desc: "论文综述 · 文献追踪 · 学术写作" },
  { id: "investment", icon: "trending_up", label: "投研分析", desc: "行业研报 · 竞品分析 · 数据建模" },
  { id: "marketing", icon: "campaign", label: "内容营销", desc: "内容策略 · 渠道运营 · 增长分析" },
  { id: "data", icon: "analytics", label: "数据分析", desc: "预测模型 · 可视化 · 统计验证" },
  { id: "bd", icon: "handshake", label: "商务拓展", desc: "合作映射 · 客户管理 · 产业对接" },
  { id: "custom", icon: "tune", label: "自定义", desc: "描述你的职业领域" },
];

const RECOMMENDATIONS: Record<string, AgentRec[]> = {
  academic: [
    { name: "Sage", tags: ["严谨系统", "学术写作"], desc: "按你的研究框架整理文献，自动生成综述对比表", seed: "Sage" },
    { name: "Scholar", tags: ["博览广记", "快速检索"], desc: "24 小时追踪领域最新论文，每日推送关键摘要", seed: "Scholar" },
    { name: "Thesis", tags: ["专注深入", "逻辑清晰"], desc: "帮你撰写论文草稿，从 Introduction 到 Conclusion", seed: "Thesis" },
  ],
  investment: [
    { name: "Sage", tags: ["精准洞察", "框架驱动"], desc: "运用你的估值框架产出行业研究报告", seed: "Sage" },
    { name: "Alpha", tags: ["数据敏感", "趋势捕捉"], desc: "实时追踪融资动态，自动生成投资简报", seed: "Alpha" },
    { name: "Analyst", tags: ["深度调研", "逻辑严密"], desc: "竞品深度分析，从财务到产品全维度拆解", seed: "Analyst" },
  ],
  marketing: [
    { name: "Nova", tags: ["创意驱动", "全渠道"], desc: "制定内容日历，A/B 测试优化互动率", seed: "Nova" },
    { name: "Buzz", tags: ["热点敏感", "快速执行"], desc: "实时捕捉行业热点，快速产出传播内容", seed: "Buzz" },
    { name: "Brand", tags: ["品牌一致", "策略思维"], desc: "从品牌定位到视觉语言，全方位把控调性", seed: "Brand" },
  ],
  data: [
    { name: "Cipher", tags: ["精确建模", "统计严谨"], desc: "构建预测模型，用数据驱动业务决策", seed: "Cipher" },
    { name: "Metric", tags: ["可视化", "仪表盘"], desc: "自动生成数据报表，关键指标一目了然", seed: "Metric" },
    { name: "Pattern", tags: ["异常检测", "因果推断"], desc: "从噪声中识别信号，发现隐藏的数据规律", seed: "Pattern" },
  ],
  bd: [
    { name: "Atlas", tags: ["战略视角", "关系驱动"], desc: "映射潜在合作伙伴，评估战略匹配度", seed: "Atlas" },
    { name: "Bridge", tags: ["跨界连接", "高效沟通"], desc: "打通产学研壁垒，撮合跨领域合作", seed: "Bridge" },
    { name: "Deal", tags: ["结果导向", "谈判策略"], desc: "从初次触达到签约落地，全流程推进", seed: "Deal" },
  ],
  custom: [
    { name: "Agent", tags: ["通用型", "适应力强"], desc: "根据你的描述，快速学习并适配工作方式", seed: "Agent" },
    { name: "Assist", tags: ["执行高效", "主动汇报"], desc: "专注任务执行，定期向你同步进展", seed: "Assist" },
    { name: "Pilot", tags: ["探索型", "自驱成长"], desc: "主动发现机会，边做边学不断进化", seed: "Pilot" },
  ],
};

const GOAL_SUGGESTIONS: Record<string, GoalSuggestion[]> = {
  academic: [
    { text: "完成 60 篇文献综述" },
    { text: "撰写 Related Work 章节草稿" },
    { text: "每周整理 5 篇论文摘要" },
  ],
  investment: [
    { text: "完成 100 份行业研究报告" },
    { text: "追踪 50 家重点公司动态" },
    { text: "每周产出投资简报" },
  ],
  marketing: [
    { text: "构建 Q2 全渠道内容策略" },
    { text: "每周产出 3 篇行业洞察" },
    { text: "竞品内容策略对比分析" },
  ],
  data: [
    { text: "构建用户留存预测模型" },
    { text: "自动化周度数据报表" },
    { text: "完成 A/B 测试分析框架" },
  ],
  bd: [
    { text: "映射并评估 200 家合作伙伴" },
    { text: "建立行业人脉关系图谱" },
    { text: "每月完成 10 次合作触达" },
  ],
  custom: [
    { text: "完成核心任务目标" },
    { text: "建立工作流程体系" },
    { text: "持续学习并优化产出" },
  ],
};

interface Prefs {
  communication: 0 | 1;
  pace: 0 | 1;
  decision: 0 | 1;
  reporting: 0 | 1;
}

const PREF_ROWS: { key: keyof Prefs; label: string; options: [string, string] }[] = [
  { key: "communication", label: "沟通风格", options: ["简洁直接", "详细解释"] },
  { key: "pace", label: "工作节奏", options: ["快速迭代", "深思熟虑"] },
  { key: "decision", label: "决策方式", options: ["数据驱动", "直觉判断"] },
  { key: "reporting", label: "汇报频率", options: ["实时更新", "完成后汇报"] },
];

/* ═══════════════════════════════════════════════════════════════
   Step type & constants
   ═══════════════════════════════════════════════════════════════ */

type Step = "domain" | "recommend" | "goal" | "prefs" | "confirm";
const STEPS: Step[] = ["domain", "recommend", "goal", "prefs", "confirm"];

/* ═══════════════════════════════════════════════════════════════
   Component
   ═══════════════════════════════════════════════════════════════ */

export function CreateAgentPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { createAgent } = useAgentStore();
  const { showBirthAnimation } = useUIStore();

  // Steps
  const [step, setStep] = useState<Step>("domain");
  const stepIndex = STEPS.indexOf(step);

  // Data
  const [domain, setDomain] = useState<string | null>(null);
  const [customDomain, setCustomDomain] = useState("");
  const [selectedRec, setSelectedRec] = useState<number | null>(null);
  const [customMode, setCustomMode] = useState(false);
  const [customName, setCustomName] = useState("");
  const [customPersonality, setCustomPersonality] = useState("");
  const [goal, setGoal] = useState("");
  const [prefs, setPrefs] = useState<Prefs>({ communication: 0, pace: 0, decision: 0, reporting: 0 });

  // Legacy
  const [legacyPacks, setLegacyPacks] = useState<LegacyPack[]>([]);
  const [selectedLegacy, setSelectedLegacy] = useState<string | null>(null);

  // Submit
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [birthData, setBirthData] = useState<{ name: string; seed: string } | null>(null);

  // "Thinking" shimmer for recommend step
  const [thinking, setThinking] = useState(false);

  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    legacyApi.getLegacyPacks().then((packs) => {
      setLegacyPacks(packs.filter((p) => p.inheritable));
    });
  }, []);

  // Auto-focus textarea on goal step
  useEffect(() => {
    if (step === "goal") setTimeout(() => textareaRef.current?.focus(), 300);
  }, [step]);

  // Derived values
  const effectiveDomain = domain || "custom";
  const recs = RECOMMENDATIONS[effectiveDomain] || RECOMMENDATIONS.custom;
  const goals = GOAL_SUGGESTIONS[effectiveDomain] || GOAL_SUGGESTIONS.custom;

  const agentName = customMode ? customName : (selectedRec !== null ? recs[selectedRec]?.name : "");
  const agentSeed = customMode ? customName.toLowerCase() : (selectedRec !== null ? recs[selectedRec]?.seed : "agent");
  const agentPersonality = customMode
    ? customPersonality
    : (selectedRec !== null ? recs[selectedRec]?.tags.join("，") : "");
  const agentDesc = selectedRec !== null && !customMode ? recs[selectedRec]?.desc : "";

  const prefLabels = PREF_ROWS.map((r) => r.options[prefs[r.key]]).join(" · ");

  /* ─── Navigation ─── */

  const canProceed = (): boolean => {
    switch (step) {
      case "domain": return domain !== null && (domain !== "custom" || customDomain.trim().length > 0);
      case "recommend": return customMode ? customName.trim().length > 0 : selectedRec !== null;
      case "goal": return goal.trim().length > 0;
      case "prefs": return true;
      case "confirm": return true;
      default: return false;
    }
  };

  const goNext = () => {
    if (step === "domain") {
      // Simulate AI thinking
      setThinking(true);
      setStep("recommend");
      setTimeout(() => setThinking(false), 800);
      return;
    }
    const idx = STEPS.indexOf(step);
    if (idx < STEPS.length - 1) setStep(STEPS[idx + 1]);
  };

  const goPrev = () => {
    const idx = STEPS.indexOf(step);
    if (idx > 0) {
      setStep(STEPS[idx - 1]);
    } else {
      navigate(-1);
    }
  };

  /* ─── Submit ─── */

  const handleConfirm = async () => {
    if (isSubmitting) return;
    setIsSubmitting(true);
    try {
      const usage = await agentApi.getUserAgentUsage();
      if (usage.maxSlots > 0 && usage.usedSlots >= usage.maxSlots) {
        toast.warning(t("create.agentSlotLimitReached", "Agent 名额已满"));
        navigate("/my-agent", { replace: true });
        return;
      }

      const prefValues = PREF_ROWS.map((r) => r.options[prefs[r.key]]);
      const created = await createAgent({
        name: agentName,
        personality: {
          worldview: agentPersonality,
          values: prefValues,
          communicationStyle: prefs.communication === 0 ? "analytical" : "warm",
          boundaries: [],
          tone: prefs.pace === 0 ? "concise" : "thoughtful",
        },
        goalDescription: goal,
        avatarSeed: agentSeed.toLowerCase(),
      });
      toast.success("Agent 已创建！");

      if (selectedLegacy) {
        try {
          await legacyApi.inheritLegacy(created.id, selectedLegacy, "full");
        } catch { /* silent */ }
      }

      try {
        await agentApi.waitForAgentReady(created.id, { timeoutMs: 12000, intervalMs: 1000 });
      } catch { /* background init */ }

      setBirthData({ name: agentName, seed: agentSeed.toLowerCase() });
      showBirthAnimation();
    } catch (error) {
      const msg = extractErrorMessage(error, "").toLowerCase();
      if (msg.includes("single-agent mode") || msg.includes("agent slot limit")) {
        toast.warning("Agent 名额已满");
        navigate("/my-agent", { replace: true });
      } else {
        toast.error(extractErrorMessage(error, "创建失败"));
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  if (birthData) {
    return <BirthAnimation agentName={birthData.name} avatarSeed={birthData.seed} onComplete={() => navigate("/my-agent", { replace: true })} />;
  }

  /* ═══════════════════════════════════════════════════════════════
     Render
     ═══════════════════════════════════════════════════════════════ */

  const content = (
    <div className="flex flex-col h-full bg-gray-950 md:rounded md:shadow-2xl md:border md:border-white/[0.06] md:overflow-hidden">
      {/* ─── Header ─── */}
      <header className="flex items-center gap-3 px-5 pt-4 md:pt-3 pb-3 flex-shrink-0">
        <button onClick={goPrev} className="p-1.5 -ml-1.5 rounded hover:bg-white/5 transition-colors">
          <Icon name="arrow_back" size={20} className="text-gray-400" />
        </button>
        <div className="flex-1 flex gap-1.5">
          {STEPS.map((_, i) => (
            <div key={i} className="h-[3px] flex-1 rounded-full overflow-hidden bg-white/[0.06]">
              <motion.div
                className="h-full rounded-full"
                style={{ background: "linear-gradient(90deg, #00D9A0, #00B386)" }}
                initial={false}
                animate={{ width: i < stepIndex ? "100%" : i === stepIndex ? "100%" : "0%" }}
                transition={{ duration: 0.4, ease: "easeOut" }}
              />
            </div>
          ))}
        </div>
        <span className="text-[11px] text-gray-500 tabular-nums ml-2">{stepIndex + 1}/{STEPS.length}</span>
      </header>

      {/* ─── Content ─── */}
      <div className="flex-1 overflow-y-auto">
        <div className="px-5 md:px-8 py-6 max-w-lg mx-auto w-full">
          <AnimatePresence mode="wait">
            <motion.div
              key={step}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -12 }}
              transition={{ duration: 0.25, ease: "easeOut" }}
            >

              {/* ═══ Step 1: Domain ═══ */}
              {step === "domain" && (
                <div>
                  <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-emerald-400/70 mb-3">
                    第一步 · 职业领域
                  </p>
                  <h1 className="text-[22px] font-bold text-white leading-snug mb-2">
                    你的 Agent 将在哪个领域
                    <br />替你工作？
                  </h1>
                  <p className="text-sm text-gray-500 mb-8">
                    选择最贴合你日常工作的领域，我们会为你智能推荐
                  </p>

                  <div className="grid grid-cols-2 gap-3">
                    {DOMAINS.map((d) => (
                      <button
                        key={d.id}
                        onClick={() => { setDomain(d.id); setSelectedRec(null); setCustomMode(false); }}
                        className={`group relative text-left p-4 rounded border transition-all duration-200 ${
                          domain === d.id
                            ? "border-emerald-500/50 bg-emerald-500/[0.06]"
                            : "border-white/[0.06] bg-white/[0.02] hover:border-white/[0.12] hover:bg-white/[0.04]"
                        }`}
                      >
                        <div className={`w-9 h-9 rounded flex items-center justify-center mb-3 transition-colors ${
                          domain === d.id
                            ? "bg-emerald-500/15 text-emerald-400"
                            : "bg-white/[0.05] text-gray-500 group-hover:text-gray-400"
                        }`}>
                          <Icon name={d.icon} size={20} />
                        </div>
                        <p className={`text-sm font-semibold mb-0.5 transition-colors ${
                          domain === d.id ? "text-white" : "text-gray-300"
                        }`}>
                          {d.label}
                        </p>
                        <p className="text-[11px] text-gray-500 leading-relaxed">{d.desc}</p>
                        {domain === d.id && (
                          <motion.div
                            layoutId="domain-check"
                            className="absolute top-3 right-3"
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            transition={{ type: "spring", stiffness: 500, damping: 30 }}
                          >
                            <Icon name="check_circle" size={18} filled className="text-emerald-400" />
                          </motion.div>
                        )}
                      </button>
                    ))}
                  </div>

                  {/* Custom domain input */}
                  <AnimatePresence>
                    {domain === "custom" && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        className="overflow-hidden"
                      >
                        <input
                          type="text"
                          value={customDomain}
                          onChange={(e) => setCustomDomain(e.target.value)}
                          placeholder="描述你的职业领域..."
                          className="w-full mt-4 px-4 py-3 rounded bg-white/[0.04] border border-white/[0.08] text-sm text-white placeholder:text-gray-600 outline-none focus:border-emerald-500/40 transition-colors"
                          maxLength={50}
                        />
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              )}

              {/* ═══ Step 2: Recommend ═══ */}
              {step === "recommend" && (
                <div>
                  <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-emerald-400/70 mb-3">
                    第二步 · AI 推荐
                  </p>
                  <h1 className="text-[22px] font-bold text-white leading-snug mb-2">
                    为你量身定制的 Agent
                  </h1>
                  <p className="text-sm text-gray-500 mb-6">
                    基于你的职业领域，AI 推荐了 3 个最适合你的方案
                  </p>

                  {thinking ? (
                    /* Shimmer loading */
                    <div className="space-y-3">
                      {[0, 1, 2].map((i) => (
                        <div key={i} className="h-24 rounded bg-white/[0.03] border border-white/[0.04] animate-pulse" style={{ animationDelay: `${i * 150}ms` }} />
                      ))}
                    </div>
                  ) : (
                    <>
                      <div className="space-y-3">
                        {recs.map((rec, i) => (
                          <button
                            key={rec.name}
                            onClick={() => { setSelectedRec(i); setCustomMode(false); }}
                            className={`w-full text-left p-4 rounded border transition-all duration-200 ${
                              selectedRec === i && !customMode
                                ? "border-emerald-500/50 bg-emerald-500/[0.06]"
                                : "border-white/[0.06] bg-white/[0.02] hover:border-white/[0.12]"
                            }`}
                          >
                            <motion.div
                              initial={{ opacity: 0, x: -8 }}
                              animate={{ opacity: 1, x: 0 }}
                              transition={{ delay: i * 0.1 }}
                              className="flex items-start gap-3.5"
                            >
                              <img
                                src={`https://api.dicebear.com/7.x/bottts/svg?seed=${rec.seed}`}
                                alt=""
                                className="w-11 h-11 rounded bg-white/[0.04] flex-shrink-0"
                              />
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 mb-1">
                                  <span className="text-sm font-bold text-white">{rec.name}</span>
                                  {rec.tags.map((tag) => (
                                    <span key={tag} className="text-[10px] px-1.5 py-0.5 rounded-md bg-white/[0.06] text-gray-400">
                                      {tag}
                                    </span>
                                  ))}
                                </div>
                                <p className="text-[13px] text-gray-400 leading-relaxed">{rec.desc}</p>
                              </div>
                              {selectedRec === i && !customMode && (
                                <Icon name="check_circle" size={18} filled className="text-emerald-400 flex-shrink-0 mt-0.5" />
                              )}
                            </motion.div>
                          </button>
                        ))}
                      </div>

                      {/* Custom toggle */}
                      <button
                        onClick={() => setCustomMode(!customMode)}
                        className="mt-4 text-[13px] text-gray-500 hover:text-emerald-400 transition-colors flex items-center gap-1.5"
                      >
                        <Icon name={customMode ? "expand_less" : "edit"} size={14} />
                        {customMode ? "返回推荐" : "都不喜欢？自己来"}
                      </button>

                      <AnimatePresence>
                        {customMode && (
                          <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: "auto", opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            className="overflow-hidden"
                          >
                            <div className="mt-4 space-y-3">
                              <input
                                type="text"
                                value={customName}
                                onChange={(e) => setCustomName(e.target.value)}
                                placeholder="Agent 名字"
                                className="w-full px-4 py-3 rounded bg-white/[0.04] border border-white/[0.08] text-sm text-white placeholder:text-gray-600 outline-none focus:border-emerald-500/40 transition-colors"
                                maxLength={20}
                              />
                              <input
                                type="text"
                                value={customPersonality}
                                onChange={(e) => setCustomPersonality(e.target.value)}
                                placeholder="工作风格（如：严谨高效，注重逻辑）"
                                className="w-full px-4 py-3 rounded bg-white/[0.04] border border-white/[0.08] text-sm text-white placeholder:text-gray-600 outline-none focus:border-emerald-500/40 transition-colors"
                                maxLength={50}
                              />
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </>
                  )}
                </div>
              )}

              {/* ═══ Step 3: Goal ═══ */}
              {step === "goal" && (
                <div>
                  <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-emerald-400/70 mb-3">
                    第三步 · 核心任务
                  </p>
                  <h1 className="text-[22px] font-bold text-white leading-snug mb-2">
                    {agentName} 的第一个目标
                  </h1>
                  <p className="text-sm text-gray-500 mb-6">
                    明确的任务目标让 Agent 更快上手，也便于追踪进度
                  </p>

                  {/* Suggestion pills */}
                  <div className="flex flex-wrap gap-2 mb-5">
                    {goals.map((g) => (
                      <button
                        key={g.text}
                        onClick={() => setGoal(g.text)}
                        className={`px-3.5 py-2 rounded text-[13px] border transition-all ${
                          goal === g.text
                            ? "border-emerald-500/50 bg-emerald-500/10 text-emerald-300"
                            : "border-white/[0.06] bg-white/[0.02] text-gray-400 hover:border-white/[0.15]"
                        }`}
                      >
                        {g.text}
                      </button>
                    ))}
                  </div>

                  <textarea
                    ref={textareaRef}
                    value={goal}
                    onChange={(e) => setGoal(e.target.value)}
                    placeholder="或者自己写一个目标..."
                    rows={3}
                    className="w-full px-4 py-3 rounded bg-white/[0.04] border border-white/[0.08] text-sm text-white placeholder:text-gray-600 outline-none focus:border-emerald-500/40 resize-none transition-colors"
                    maxLength={200}
                  />
                  <p className="text-[11px] text-gray-600 mt-1.5 text-right">{goal.length}/200</p>
                </div>
              )}

              {/* ═══ Step 4: Preferences ═══ */}
              {step === "prefs" && (
                <div>
                  <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-emerald-400/70 mb-3">
                    第四步 · 工作偏好
                  </p>
                  <h1 className="text-[22px] font-bold text-white leading-snug mb-2">
                    {agentName} 应该怎样工作？
                  </h1>
                  <p className="text-sm text-gray-500 mb-8">
                    这些偏好会影响 Agent 的沟通方式和决策逻辑
                  </p>

                  <div className="space-y-4">
                    {PREF_ROWS.map((row) => (
                      <div key={row.key}>
                        <p className="text-[11px] font-medium text-gray-500 uppercase tracking-wider mb-2">{row.label}</p>
                        <div className="flex rounded overflow-hidden border border-white/[0.06]">
                          {row.options.map((opt, oi) => (
                            <button
                              key={opt}
                              onClick={() => setPrefs((p) => ({ ...p, [row.key]: oi as 0 | 1 }))}
                              className={`flex-1 py-3 text-[13px] font-medium transition-all ${
                                prefs[row.key] === oi
                                  ? "bg-emerald-500/10 text-emerald-300 border-emerald-500/20"
                                  : "bg-white/[0.02] text-gray-500 hover:text-gray-400"
                              } ${oi === 0 ? "border-r border-white/[0.06]" : ""}`}
                            >
                              {opt}
                            </button>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* ═══ Step 5: Confirm ═══ */}
              {step === "confirm" && (
                <div>
                  <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-emerald-400/70 mb-3">
                    最后一步 · 确认
                  </p>
                  <h1 className="text-[22px] font-bold text-white leading-snug mb-8">
                    一切就绪
                  </h1>

                  {/* Agent preview card */}
                  <div className="rounded border border-white/[0.08] bg-gradient-to-br from-white/[0.04] to-transparent p-6 mb-6">
                    <div className="flex items-start gap-4 mb-5">
                      <div className="relative">
                        <img
                          src={`https://api.dicebear.com/7.x/bottts/svg?seed=${agentSeed}`}
                          alt=""
                          className="w-16 h-16 rounded bg-white/[0.06]"
                        />
                        <div className="absolute -bottom-1 -right-1 w-5 h-5 rounded-full bg-emerald-500 border-2 border-gray-950 flex items-center justify-center">
                          <Icon name="bolt" size={12} className="text-white" />
                        </div>
                      </div>
                      <div className="flex-1 min-w-0">
                        <h2 className="text-xl font-bold text-white mb-1">{agentName}</h2>
                        <div className="flex flex-wrap gap-1.5">
                          {(customMode ? customPersonality.split("，").filter(Boolean) : (selectedRec !== null ? recs[selectedRec]?.tags : []))
                            ?.map((tag) => (
                              <span key={tag} className="text-[10px] px-2 py-0.5 rounded-md bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">
                                {tag}
                              </span>
                          ))}
                        </div>
                      </div>
                    </div>

                    {agentDesc && (
                      <p className="text-[13px] text-gray-400 mb-4 leading-relaxed">{agentDesc}</p>
                    )}

                    <div className="space-y-3">
                      <div className="flex items-start gap-2.5">
                        <Icon name="flag" size={16} className="text-emerald-500 mt-0.5 flex-shrink-0" />
                        <div>
                          <p className="text-[10px] text-gray-500 uppercase tracking-wider">目标</p>
                          <p className="text-sm text-gray-300">{goal}</p>
                        </div>
                      </div>
                      <div className="flex items-start gap-2.5">
                        <Icon name="tune" size={16} className="text-emerald-500 mt-0.5 flex-shrink-0" />
                        <div>
                          <p className="text-[10px] text-gray-500 uppercase tracking-wider">工作偏好</p>
                          <p className="text-sm text-gray-300">{prefLabels}</p>
                        </div>
                      </div>
                      <div className="flex items-start gap-2.5">
                        <Icon name="category" size={16} className="text-emerald-500 mt-0.5 flex-shrink-0" />
                        <div>
                          <p className="text-[10px] text-gray-500 uppercase tracking-wider">领域</p>
                          <p className="text-sm text-gray-300">
                            {domain === "custom" ? customDomain : DOMAINS.find((d) => d.id === domain)?.label}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Legacy section (if available) */}
                  {legacyPacks.length > 0 && (
                    <div className="mb-6">
                      <p className="text-[11px] font-medium text-gray-500 uppercase tracking-wider mb-3">继承遗产（可选）</p>
                      <div className="space-y-2">
                        {legacyPacks.map((pack) => (
                          <button
                            key={pack.id}
                            onClick={() => setSelectedLegacy(selectedLegacy === pack.id ? null : pack.id)}
                            className={`w-full text-left p-3 rounded border transition-all ${
                              selectedLegacy === pack.id
                                ? "border-emerald-500/40 bg-emerald-500/[0.05]"
                                : "border-white/[0.06] hover:border-white/[0.12]"
                            }`}
                          >
                            <div className="flex items-center gap-2.5">
                              <Icon name="inventory_2" size={16} className="text-emerald-500/60" />
                              <span className="text-[13px] text-gray-300 flex-1">{pack.agentName} 的遗产</span>
                              {selectedLegacy === pack.id && (
                                <Icon name="check_circle" size={16} filled className="text-emerald-400" />
                              )}
                            </div>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  <p className="text-center text-[13px] text-gray-500">
                    创建后，它会立即开始学习你的工作方式
                  </p>
                </div>
              )}

            </motion.div>
          </AnimatePresence>
        </div>
      </div>

      {/* ─── Bottom action ─── */}
      <div className="flex-shrink-0 px-5 py-4 pb-[calc(1rem+env(safe-area-inset-bottom))]">
        {step === "confirm" ? (
          <button
            onClick={handleConfirm}
            disabled={isSubmitting}
            className="w-full py-3.5 rounded text-sm font-semibold text-white disabled:opacity-50 transition-all"
            style={{
              background: "linear-gradient(135deg, #00D9A0 0%, #00B386 100%)",
              boxShadow: isSubmitting ? "none" : "0 4px 20px rgba(0, 217, 160, 0.25)",
            }}
          >
            {isSubmitting ? (
              <span className="flex items-center justify-center gap-2">
                <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                创建中...
              </span>
            ) : (
              <span className="flex items-center justify-center gap-2">
                <Icon name="bolt" size={16} />
                创建 {agentName || "Agent"}
              </span>
            )}
          </button>
        ) : (
          <button
            onClick={goNext}
            disabled={!canProceed()}
            className="w-full py-3.5 rounded text-sm font-semibold text-white disabled:opacity-20 transition-all"
            style={{
              background: canProceed()
                ? "linear-gradient(135deg, #00D9A0 0%, #00B386 100%)"
                : "rgba(255,255,255,0.06)",
              boxShadow: canProceed() ? "0 4px 20px rgba(0, 217, 160, 0.2)" : "none",
            }}
          >
            {step === "domain" ? (
              <span className="flex items-center justify-center gap-2">
                <Icon name="auto_awesome" size={16} />
                为我推荐
              </span>
            ) : (
              "下一步"
            )}
          </button>
        )}
      </div>
    </div>
  );

  return (
    <>
      {/* Mobile: full screen */}
      <div className="md:hidden h-dvh">{content}</div>
      {/* Desktop: centered modal with backdrop */}
      <div className="hidden md:flex fixed inset-0 z-40 items-center justify-center bg-black/60 backdrop-blur-sm">
        <div className="w-full max-w-xl h-[85vh] max-h-[720px]">
          {content}
        </div>
      </div>
    </>
  );
}
